import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  Input,
  Spinner,
  Text,
  chakra,
  useToast,
} from '@chakra-ui/react';
import {
  Branch,
  branchesApi,
  DeliveryMethod,
  DeliveryTerm,
  PaymentTerm,
} from '@texas/api/endpoints/branchesApi';
import { Currency } from '@texas/api/endpoints/currenciesApi';
import {
  countriesApi,
  Country,
} from '@texas/api/endpoints/metadata/countriesApi';
import {
  SupplierBranchData,
  suppliersApi,
  UpdateSupplierBranchDataRequest,
} from '@texas/api/endpoints/suppliersApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { setValueAsDecimalExtender } from '@texas/components/article/variant/sales/shared';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { TexasSwitch } from '@texas/components/shared/form/TexasSwitch';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import {
  getEntityWorkspaceListItemStyle,
  entityWorkspaceStyles,
} from '@texas/resources/theme/styles/entityWorkspace';
import { ReactSelectOption } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { reactEvents } from '@bridge/reactEvents';

export function SupplierBranchInfo({
  branchData,
  supplierId,
}: {
  branchData: SupplierBranchData[];
  supplierId: number;
}) {
  const { t } = useTranslation();

  const {
    branchesData: [branches, getBranches, loadingBranches],
  } = useMetadata();

  useEffect(() => {
    getBranches();
  }, [getBranches]);

  const branchViews = useMemo(
    () =>
      branches
        .map((branch) => {
          const data =
            branchData.find((item) => item.branchId === branch.id) ?? null;

          return {
            branch,
            data,
            active: data?.enabledAt != null,
          };
        })
        .sort((a, b) => Number(b.active) - Number(a.active)),
    [branchData, branches],
  );
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);

  useEffect(() => {
    setSelectedBranchId((currentBranchId) => {
      if (branchViews.some(({ branch }) => branch.id === currentBranchId)) {
        return currentBranchId;
      }

      return branchViews[0]?.branch.id ?? null;
    });
  }, [branchViews]);

  const activeBranches = branchViews.filter(({ active }) => active);
  const inactiveBranches = branchViews.filter(({ active }) => !active);
  const selectedBranch = branchViews.find(
    ({ branch }) => branch.id === selectedBranchId,
  );

  return (
    <Box sx={entityWorkspaceStyles.branchPage}>
      {loadingBranches ? (
        <Spinner />
      ) : (
        <Flex sx={entityWorkspaceStyles.branchOverview}>
          <Box as="aside" sx={entityWorkspaceStyles.branchSelector}>
            <Text sx={entityWorkspaceStyles.branchSelectorTitle}>
              {t('general.branches')}
            </Text>
            {activeBranches.length > 0 && (
              <BranchGroup
                label={t('suppliers.workspace.activeBranches', {
                  count: activeBranches.length,
                })}
                branches={activeBranches}
                selectedBranchId={selectedBranchId}
                onSelect={setSelectedBranchId}
              />
            )}
            {inactiveBranches.length > 0 && (
              <BranchGroup
                label={t('suppliers.workspace.inactiveBranches', {
                  count: inactiveBranches.length,
                })}
                branches={inactiveBranches}
                selectedBranchId={selectedBranchId}
                onSelect={setSelectedBranchId}
                separated={activeBranches.length > 0}
              />
            )}
          </Box>
          {selectedBranch && (
            <BranchInfo
              key={selectedBranch.branch.id}
              supplierBranchData={selectedBranch.data}
              supplierId={supplierId}
              branchId={selectedBranch.branch.id}
              branchIdentifier={selectedBranch.branch.identifier}
              branchName={selectedBranch.branch.name}
              branchActive={selectedBranch.active}
              defaultCurrencyId={selectedBranch.branch.defaultCurrencyId}
            />
          )}
        </Flex>
      )}
    </Box>
  );
}

interface BranchView {
  branch: Branch;
  data: SupplierBranchData | null;
  active: boolean;
}

function BranchGroup({
  label,
  branches,
  selectedBranchId,
  onSelect,
  separated = false,
}: {
  label: string;
  branches: BranchView[];
  selectedBranchId: number | null;
  onSelect: (branchId: number) => void;
  separated?: boolean;
}) {
  return (
    <Box
      sx={
        separated
          ? entityWorkspaceStyles.branchGroupSeparated
          : entityWorkspaceStyles.branchGroup
      }
    >
      <Text sx={entityWorkspaceStyles.branchGroupLabel}>{label}</Text>
      <Flex sx={entityWorkspaceStyles.branchItems}>
        {branches.map(({ branch, data, active }) => {
          const selected = branch.id === selectedBranchId;
          const supplierNumber = data?.supplierNumber?.trim();

          return (
            <Box
              as="button"
              key={branch.id}
              type="button"
              aria-pressed={selected}
              onClick={() => onSelect(branch.id)}
              sx={getEntityWorkspaceListItemStyle(active, selected)}
            >
              <Text sx={entityWorkspaceStyles.branchItemName}>
                {branch.identifier} - {branch.name}
              </Text>
              <Text sx={entityWorkspaceStyles.branchItemNumber}>
                {supplierNumber?.length ? supplierNumber : '—'}
              </Text>
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
}

function BranchInfo({
  supplierBranchData,
  supplierId,
  branchId,
  branchIdentifier,
  branchName,
  branchActive,
  defaultCurrencyId,
}: {
  supplierBranchData: SupplierBranchData | null;
  supplierId: number;
  branchId: number;
  branchIdentifier: Branch['identifier'];
  branchName: string;
  branchActive: boolean;
  defaultCurrencyId: number;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [selectedCurrency, setSelectedCurrency] = useState<
    Currency | undefined
  >(undefined);

  const {
    data: countries,
    refetch: refetchCountries,
    loading: loadingCountries,
  } = useApiResource(countriesApi.getCountries);

  const {
    data: paymentTerms,
    refetch: refetchPaymentTerms,
    loading: loadingPaymentTerms,
  } = useApiResource(branchesApi.getPaymentTermsForBranch);

  const {
    data: deliveryTerms,
    refetch: refetchDeliveryTerms,
    loading: loadingDeliveryTerms,
  } = useApiResource(branchesApi.getDeliveryTermsForBranch);

  const {
    data: deliveryMethods,
    refetch: refetchDeliveryMethods,
    loading: loadingDeliveryMethods,
  } = useApiResource(branchesApi.getDeliveryMethodsForBranch);

  const {
    data: currencies,
    refetch: refetchCurrencies,
    loading: loadingCurrencies,
  } = useApiResource(branchesApi.getCurrenciesForBranch);

  useEffect(() => {
    refetchCountries(branchId);
    refetchPaymentTerms(branchId);
    refetchDeliveryTerms(branchId);
    refetchDeliveryMethods(branchId);
    refetchCurrencies(branchId);
  }, [
    refetchCountries,
    refetchPaymentTerms,
    refetchDeliveryTerms,
    refetchDeliveryMethods,
    refetchCurrencies,
    branchId,
  ]);

  useEffect(() => {
    setSelectedCurrency(
      currencies?.find((c) => c.id === supplierBranchData?.currencyId),
    );
  }, [supplierBranchData, currencies]);

  const { register, reset, handleSubmit, formState, control, watch, setValue } =
    useForm<UpdateSupplierBranchDataRequest>({
      defaultValues: {
        enabled: supplierBranchData?.enabledAt != null,
        supplierNumber: supplierBranchData?.supplierNumber ?? null,
        address1: supplierBranchData?.address1 ?? null,
        address2: supplierBranchData?.address2 ?? null,
        city: supplierBranchData?.city ?? null,
        telephone: supplierBranchData?.telephone ?? null,
        email: supplierBranchData?.email ?? null,
        website: supplierBranchData?.website ?? null,
        countryId: supplierBranchData?.countryId ?? null,
        deliveryMethodId: supplierBranchData?.deliveryMethodId ?? null,
        deliveryTermId: supplierBranchData?.deliveryTermId ?? null,
        paymentTermId: supplierBranchData?.paymentTermId ?? null,
        currencyId: supplierBranchData?.currencyId ?? null,
        buyingRate: supplierBranchData?.buyingRate ?? null,
        useCustomBuyingRate: supplierBranchData?.useCustomBuyingRate ?? false,
      },
    });

  useEffect(() => {
    reset({
      enabled: supplierBranchData?.enabledAt != null,
      supplierNumber: supplierBranchData?.supplierNumber ?? null,
      address1: supplierBranchData?.address1 ?? null,
      address2: supplierBranchData?.address2 ?? null,
      city: supplierBranchData?.city ?? null,
      telephone: supplierBranchData?.telephone ?? null,
      email: supplierBranchData?.email ?? null,
      website: supplierBranchData?.website ?? null,
      countryId: supplierBranchData?.countryId ?? null,
      deliveryMethodId: supplierBranchData?.deliveryMethodId ?? null,
      deliveryTermId: supplierBranchData?.deliveryTermId ?? null,
      paymentTermId: supplierBranchData?.paymentTermId ?? null,
      currencyId: supplierBranchData?.currencyId ?? null,
      buyingRate: supplierBranchData?.buyingRate ?? null,
      useCustomBuyingRate: supplierBranchData?.useCustomBuyingRate ?? false,
    });
  }, [reset, supplierBranchData]);

  const { request: updateBranchRequest, error: updateBranchError } =
    useApiRequest(suppliersApi.updateBranchInfo);

  const useCustomBuyingRate = watch('useCustomBuyingRate');
  const buyingRate = watch('buyingRate');

  const handleCurrencyChange = (
    value: ReactSelectOption | null,
    onChange: (value: number | null) => void,
  ) => {
    const currency =
      currencies?.find((item) => item.id === value?.value) ?? undefined;
    setSelectedCurrency(currency);
    onChange(currency?.id ?? null);
    setValue('buyingRate', currency?.buyingRate ?? null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('useCustomBuyingRate', false, { shouldDirty: true });
  };

  const submit = async (data: UpdateSupplierBranchDataRequest) =>
    await request(
      updateBranchRequest,
      [data, supplierId, branchId],
      (updatedBranchData) => {
        reset({
          ...updatedBranchData,
          enabled: updatedBranchData.enabledAt != null,
        });
        reactEvents.supplierBranchUpdated.dispatch(supplierId);
        toast({
          title: t('general.updated'),
          status: 'success',
          isClosable: true,
        });
      },
    );

  return (
    <chakra.form
      onSubmit={handleSubmit(submit)}
      sx={entityWorkspaceStyles.branchDetails}
    >
      <ErrorDetails error={updateBranchError} />
      <Flex sx={entityWorkspaceStyles.branchDetailsHeader}>
        <Box>
          <Text sx={entityWorkspaceStyles.branchDetailsTitle}>
            {branchIdentifier} - {branchName}
          </Text>
          <Text sx={entityWorkspaceStyles.branchDetailsDescription}>
            {t('suppliers.workspace.branchDetails')}
          </Text>
        </Box>
        <Flex align="center" gap={4}>
          <Badge
            variant={
              branchActive ? 'entity-status-active' : 'entity-status-inactive'
            }
          >
            {branchActive
              ? t('complianceDocumentDefinitions.active')
              : t('suppliers.workspace.inactive')}
          </Badge>
          <Controller
            name="enabled"
            control={control}
            render={({ field }) => (
              <TexasSwitch
                checked={field.value}
                label={t('suppliers.management.enabledForSupplier')}
                onChange={field.onChange}
              />
            )}
          />
        </Flex>
      </Flex>
      <Flex sx={entityWorkspaceStyles.branchFields}>
        <Flex sx={entityWorkspaceStyles.branchFieldRow}>
          <FormControl
            sx={entityWorkspaceStyles.branchField}
            isInvalid={!!formState.errors.supplierNumber}
          >
            <TexasFormLabel>
              {t('suppliers.management.supplierNumber')}
            </TexasFormLabel>
            <Input
              {...register('supplierNumber', {
                onChange: (event: ChangeEvent<HTMLInputElement>) =>
                  setValue('enabled', event.target.value.trim().length > 0, {
                    shouldDirty: true,
                    shouldValidate: true,
                  }),
              })}
              variant="entityDirectory"
            />
            <ErrorLabel text={formState.errors.supplierNumber?.message} />
          </FormControl>
          <FormControl
            sx={entityWorkspaceStyles.branchField}
            isInvalid={!!formState.errors.telephone}
          >
            <TexasFormLabel>{t('general.telephone')}</TexasFormLabel>
            <Input {...register('telephone')} variant="entityDirectory" />
            <ErrorLabel text={formState.errors.telephone?.message} />
          </FormControl>
        </Flex>
        <Flex sx={entityWorkspaceStyles.branchFieldRow}>
          <FormControl
            sx={entityWorkspaceStyles.branchField}
            isInvalid={!!formState.errors.address1}
          >
            <TexasFormLabel>
              {t('suppliers.management.address1')}
            </TexasFormLabel>
            <Input {...register('address1')} variant="entityDirectory" />
            <ErrorLabel text={formState.errors.address1?.message} />
          </FormControl>
          <FormControl
            sx={entityWorkspaceStyles.branchField}
            isInvalid={!!formState.errors.city}
          >
            <TexasFormLabel>{t('suppliers.management.city')}</TexasFormLabel>
            <Input {...register('city')} variant="entityDirectory" />
            <ErrorLabel text={formState.errors.city?.message} />
          </FormControl>
        </Flex>
        <Flex sx={entityWorkspaceStyles.branchFieldRow}>
          <FormControl
            sx={entityWorkspaceStyles.branchField}
            isInvalid={!!formState.errors.address2}
          >
            <TexasFormLabel>
              {t('suppliers.management.address2')}
            </TexasFormLabel>
            <Input {...register('address2')} variant="entityDirectory" />
            <ErrorLabel text={formState.errors.address2?.message} />
          </FormControl>
          <FormControl
            sx={entityWorkspaceStyles.branchField}
            isInvalid={!!formState.errors.countryId}
          >
            <TexasFormLabel>{t('project.country')}</TexasFormLabel>
            <Controller
              name="countryId"
              control={control}
              render={({ field }) => (
                <TexasSelect
                  {...field}
                  classNamePrefix="supplier-branch-select"
                  loading={loadingCountries}
                  value={findCountry(field.value, countries)}
                  onChange={(value) => field.onChange(value?.value ?? null)}
                  isClearable={true}
                  menuPlacement="top"
                  options={countries?.map<ReactSelectOption>((c) => {
                    return {
                      label: `${c.name} (${c.code})`,
                      value: c.id,
                    };
                  })}
                />
              )}
            />
            <ErrorLabel text={formState.errors.countryId?.message} />
          </FormControl>
        </Flex>
        <Flex sx={entityWorkspaceStyles.branchFieldRow}>
          <FormControl
            sx={entityWorkspaceStyles.branchField}
            isInvalid={!!formState.errors.website}
          >
            <TexasFormLabel>{t('general.website')}</TexasFormLabel>
            <Input {...register('website')} variant="entityDirectory" />
            <ErrorLabel text={formState.errors.website?.message} />
          </FormControl>
          <FormControl
            sx={entityWorkspaceStyles.branchField}
            isInvalid={!!formState.errors.email}
          >
            <TexasFormLabel>{t('general.email')}</TexasFormLabel>
            <Input {...register('email')} variant="entityDirectory" />
            <ErrorLabel text={formState.errors.email?.message} />
          </FormControl>
        </Flex>
        <Flex sx={entityWorkspaceStyles.branchFieldRow}>
          <FormControl
            sx={entityWorkspaceStyles.branchField}
            isInvalid={!!formState.errors.paymentTermId}
          >
            <TexasFormLabel>
              {t('suppliers.management.paymentTerms')}
            </TexasFormLabel>
            <Controller
              name="paymentTermId"
              control={control}
              render={({ field }) => (
                <TexasSelect
                  {...field}
                  classNamePrefix="supplier-branch-select"
                  loading={loadingPaymentTerms}
                  value={findOption(field.value, paymentTerms)}
                  onChange={(value) => field.onChange(value?.value ?? null)}
                  isClearable={true}
                  menuPlacement="top"
                  options={paymentTerms?.map<ReactSelectOption>((c) => {
                    return {
                      label: c.name,
                      value: c.id,
                    };
                  })}
                />
              )}
            />
            <ErrorLabel text={formState.errors.paymentTermId?.message} />
          </FormControl>
          <FormControl
            sx={entityWorkspaceStyles.branchField}
            isInvalid={!!formState.errors.deliveryTermId}
          >
            <TexasFormLabel>
              {t('suppliers.management.deliveryTerms')}
            </TexasFormLabel>
            <Controller
              name="deliveryTermId"
              control={control}
              render={({ field }) => (
                <TexasSelect
                  {...field}
                  classNamePrefix="supplier-branch-select"
                  loading={loadingDeliveryTerms}
                  value={findOption(field.value, deliveryTerms)}
                  onChange={(value) => field.onChange(value?.value ?? null)}
                  isClearable={true}
                  menuPlacement="top"
                  options={deliveryTerms?.map<ReactSelectOption>((c) => {
                    return {
                      label: c.name,
                      value: c.id,
                    };
                  })}
                />
              )}
            />
            <ErrorLabel text={formState.errors.deliveryTermId?.message} />
          </FormControl>
        </Flex>
        <Flex sx={entityWorkspaceStyles.branchFieldRow}>
          <FormControl
            sx={entityWorkspaceStyles.branchField}
            isInvalid={!!formState.errors.deliveryMethodId}
          >
            <TexasFormLabel>
              {t('suppliers.management.deliveryMethods')}
            </TexasFormLabel>
            <Controller
              name="deliveryMethodId"
              control={control}
              render={({ field }) => (
                <TexasSelect
                  {...field}
                  classNamePrefix="supplier-branch-select"
                  loading={loadingDeliveryMethods}
                  value={findOption(field.value, deliveryMethods)}
                  onChange={(value) => field.onChange(value?.value ?? null)}
                  isClearable={true}
                  menuPlacement="top"
                  options={deliveryMethods?.map<ReactSelectOption>((c) => {
                    return {
                      label: c.name,
                      value: c.id,
                    };
                  })}
                />
              )}
            />
            <ErrorLabel text={formState.errors.deliveryMethodId?.message} />
          </FormControl>
          <FormControl
            sx={entityWorkspaceStyles.branchField}
            isInvalid={!!formState.errors.currencyId}
          >
            <TexasFormLabel>
              {t('suppliers.management.currency')}
            </TexasFormLabel>
            <Controller
              name="currencyId"
              control={control}
              render={({ field }) => (
                <TexasSelect
                  {...field}
                  classNamePrefix="supplier-branch-select"
                  loading={loadingCurrencies}
                  value={findCurrency(field.value, currencies)}
                  onChange={(value) =>
                    handleCurrencyChange(value, field.onChange)
                  }
                  isClearable={true}
                  onClear={() => setSelectedCurrency(undefined)}
                  menuPlacement="top"
                  options={currencies?.map<ReactSelectOption>((c) => {
                    return {
                      label: `${c.name} (${c.code})`,
                      value: c.id,
                    };
                  })}
                />
              )}
            />
            <ErrorLabel text={formState.errors.currencyId?.message} />
          </FormControl>
        </Flex>
      </Flex>
      <Flex sx={entityWorkspaceStyles.branchFooter}>
        {selectedCurrency ? (
          <FormControl
            sx={entityWorkspaceStyles.branchRateControl}
            isInvalid={!!formState.errors.buyingRate}
          >
            <Flex sx={entityWorkspaceStyles.branchRateContent}>
              <Box sx={entityWorkspaceStyles.branchRateSummary}>
                <Text sx={entityWorkspaceStyles.branchRateTitle}>
                  {t('suppliers.management.buyingRate')} ·{' '}
                  {(useCustomBuyingRate
                    ? buyingRate
                    : selectedCurrency.buyingRate) ?? '—'}
                </Text>
                {useCustomBuyingRate ? (
                  <Input
                    {...register('buyingRate', {
                      min: { value: 0, message: t('errors.notNegative') },
                      setValueAs: (value) =>
                        setValueAsDecimalExtender(value, true),
                    })}
                    variant="entityDirectory"
                    sx={entityWorkspaceStyles.branchRateInput}
                  />
                ) : (
                  <Text sx={entityWorkspaceStyles.branchRateDescription}>
                    {t('suppliers.management.usingGarpBuyingRateShort')}
                  </Text>
                )}
              </Box>
              <Button
                variant="link"
                sx={entityWorkspaceStyles.branchRateToggle}
                isDisabled={
                  formState.isSubmitting ||
                  selectedCurrency.id === defaultCurrencyId
                }
                onClick={() => {
                  const nextUseCustomBuyingRate = !useCustomBuyingRate;
                  setValue('useCustomBuyingRate', nextUseCustomBuyingRate, {
                    shouldDirty: true,
                  });
                  setValue(
                    'buyingRate',
                    nextUseCustomBuyingRate
                      ? selectedCurrency.buyingRate
                      : null,
                    { shouldDirty: true, shouldValidate: true },
                  );
                }}
              >
                {useCustomBuyingRate
                  ? t('suppliers.management.useGarpBuyingRate')
                  : t('suppliers.management.useCustomBuyingRate')}
              </Button>
            </Flex>
            <ErrorLabel text={formState.errors.buyingRate?.message} />
          </FormControl>
        ) : (
          <Box sx={entityWorkspaceStyles.branchFooterSpacer} />
        )}
        <Button
          type="submit"
          variant="entityDirectoryPrimary"
          isDisabled={!formState.isDirty || formState.isSubmitting}
          isLoading={formState.isSubmitting}
        >
          {t('complianceDocumentDefinitions.saveChanges')}
        </Button>
      </Flex>
    </chakra.form>
  );
}

function findCountry(id: number | null, countries: Country[] | null) {
  if (!countries) return undefined;
  const country = countries.find((c) => c.id === id);
  if (country)
    return {
      label: `${country.name} (${country.code})`,
      value: country.id,
    };

  return undefined;
}

function findCurrency(id: number | null, currencies: Currency[] | null) {
  if (!currencies) return undefined;
  const currency = currencies.find((c) => c.id === id);
  if (currency)
    return {
      label: currency.code,
      value: currency.id,
    };

  return undefined;
}

function findOption(
  id: number | null,
  options: PaymentTerm[] | DeliveryTerm[] | DeliveryMethod[] | null,
) {
  if (!options) return undefined;
  const option = options.find((pt) => pt.id === id);
  if (option)
    return {
      label: option.name,
      value: option.id,
    };

  return undefined;
}
